import { InternalRefetchQueryDescriptor } from "@apollo/client";
import { ModalOpenButton } from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { merge } from "lodash-es";
import React from "react";
import { type To, useLocation, useNavigate } from "react-router-dom";
import { makeRefetchQuery } from "../../../clients/apollo-client-refetch-queries.js";
import { Agreement, PermissionName } from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { RestrictedByCapabilityAndDocumentPermissionWithDebug } from "../../auth/RestrictedByCapabilityAndDocumentPermission.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { ManualSave } from "../../commons/form-fields/ManualSave.js";
import { ViewMode } from "../../commons/ViewModeMenuItem.js";
import { QUOTE_ITEM_TYPES } from "../../constants.js";
import { BareTreeItem } from "../../trees/BareTreeItem.js";
import { TreeRightIcons } from "../../trees/components/TreeRightIcons.js";
import { ContentBox } from "../contents/ContentBox.js";
import { CustomFieldsBox } from "../custom-fields/CustomFieldsBox.js";
import { EditCustomFieldModal } from "../custom-fields/EditCustomFieldModal.js";
import { itemHasEstimatedCalculationBox } from "../doc-items/app-conditions/boxes.js";
import { BuildingPlacementBox } from "../doc-items/boxes/BuildingPlacementBox.js";
import { PendingChangesBox } from "../doc-items/boxes/debug/PendingChangesBox.js";
import { DecisionManagementBox } from "../doc-items/boxes/DecisionManagementBox.js";
import {
  EstimatedCalculationBox,
  FormValues,
} from "../doc-items/boxes/EstimatedCalculationBox.js";
import { HeaderBox } from "../doc-items/boxes/HeaderBox.js";
import { ItemFilesBox } from "../doc-items/boxes/ItemFilesBox.js";
import { ProductBox } from "../doc-items/boxes/ProductBox.js";
import { RecommendationsBox } from "../doc-items/boxes/RecommendationsBox.js";
import { RequiredOnCompletionBox } from "../doc-items/boxes/RequiredOnCompletionBox.js";
import { RulesBox } from "../doc-items/boxes/RulesBox.js";
import {
  SubelementsBox,
  SubitemComponentProps,
} from "../doc-items/boxes/SubelementsBox.js";
import { TaskManagementBox } from "../doc-items/boxes/TaskManagementBox.js";
import { TemplateTypePlaceholderBox } from "../doc-items/boxes/TemplateTypePlaceholderBox.js";
import { useProposedCalculationsWithOverride } from "../doc-items/useProposedCalculationsWithOverride.js";
import { getData123 } from "../projects/quote-trees.js";
import { RelatedOrderItemsBox } from "../purchase-orders/boxes/RelatedOrderItemsBox.js";
import {
  S4HanaQuoteItemDataBoxDocument,
  S4HanaQuoteItemDataBoxQueryVariables,
} from "../sap-s4-hana/S4HanaQuoteItemDataBox.generated.js";
import { S4HanaQuoteItemDataBox } from "../sap-s4-hana/S4HanaQuoteItemDataBox.js";
import { useProjectQuoteContext } from "../../main-routes/projects/ProjectQuoteContext.js";
import { itemCanBeEdited } from "./helpers.js";
import {
  QuoteItem_ChildItemComponentFragment,
  QuoteItem_ChildItemFragment,
  QuoteItem_ItemFragment,
  QuoteItem_ProjectFragment,
  QuoteItem_QuoteFragment,
} from "./QuoteItem.generated.js";
import { RfQuoteItemAttachmentsBox } from "../sap-s4-hana/richter-frenzel/RfQuoteItemAttachmentsBox.js";

interface Props {
  viewMode: ViewMode;
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  isManualSave: boolean;
  project: QuoteItem_ProjectFragment;
  quote: QuoteItem_QuoteFragment;
  item: QuoteItem_ItemFragment;
  pathToDocList: string;
  pathToDocPage: string;
  onChangeCustomFieldsRefetchQueries: string[];
  onSetContentRefetchQueries: string[];
  onUpdateDataRefetchQueries?: InternalRefetchQueryDescriptor[];
  canEdit: boolean;
  expandedItemIds: string[] | undefined;
  hasSapS4HanaIntegration: boolean;
}

export const QuoteItem = ({
  viewMode,
  setViewMode,
  isManualSave,
  project,
  quote,
  item,
  onChangeCustomFieldsRefetchQueries,
  onSetContentRefetchQueries,
  onUpdateDataRefetchQueries,
  pathToDocPage,
  canEdit,
  expandedItemIds,
  hasSapS4HanaIntegration,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate("QuoteItem");
  const showDebugOutput = useFeature("DebugOutput");
  const { refetchWizardQuestions } = useProjectQuoteContext();

  const canManualSave =
    item.type === "paid" || item.type === "section" || item.type === "decision";

  const hasEstimatedCalculationBox = itemHasEstimatedCalculationBox(item);
  const canBeEdited = itemCanBeEdited(item);
  const agreement: Agreement | undefined = quote?.agreement;

  const ChildItemComponent = React.useMemo(
    () =>
      agreement !== undefined
        ? createChildItemComponent(
            pathToDocPage,
            { viewerPermissions: quote?.viewerPermissions ?? [] },
            agreement,
            itemProps => ({
              pathname: `${pathToDocPage}/items/${itemProps.item.id}`,
              search: location.search,
            })
          )
        : () => null,
    [agreement, location.search, pathToDocPage, quote?.viewerPermissions]
  );

  const productBoxOnUpdateDataRefetchQueries = React.useMemo(() => {
    const refetchQueries = onUpdateDataRefetchQueries ?? [];
    if (hasSapS4HanaIntegration) {
      return [
        ...refetchQueries,
        makeRefetchQuery<S4HanaQuoteItemDataBoxQueryVariables>({
          document: S4HanaQuoteItemDataBoxDocument,
          variables: {
            projectId: project.id,
            itemId: item.id,
            quoteId: quote.id,
          },
        }),
      ];
    }

    return refetchQueries;
  }, [
    hasSapS4HanaIntegration,
    item.id,
    onUpdateDataRefetchQueries,
    project.id,
    quote.id,
  ]);

  return (
    <Stack direction="column" spacing={1}>
      {showDebugOutput && (
        <>
          <PendingChangesBox
            itemId={item.id}
            docId={quote.id}
            projectId={project.id}
          />
        </>
      )}

      <HeaderBox
        projectId={project.id}
        docId={quote.id}
        docType="QUOTE"
        item={item}
        viewMode={canEdit ? viewMode : null}
        setViewMode={setViewMode}
        isManualSave={canManualSave && isManualSave}
        onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
        documentViewerPermissions={quote.viewerPermissions}
      />

      {!item.isRootItem && item.type !== "unpaid" && (
        <RulesBox
          projectId={project.id}
          docId={quote.id}
          item={item}
          canEdit={canEdit}
          docType="QUOTE"
          documentViewerPermissions={quote.viewerPermissions}
          refetchWizardQuestions={refetchWizardQuestions}
        />
      )}

      {item.type === "paid" && canBeEdited && (
        <ProductBox
          project={project}
          docId={quote.id}
          docType="QUOTE"
          documentViewerPermissions={quote.viewerPermissions}
          item={item}
          isEditable={canEdit}
          viewerIsContractor={quote.contractor.isMyOrganisation}
          onUpdateDataRefetchQueries={productBoxOnUpdateDataRefetchQueries}
          refetchWizardQuestions={refetchWizardQuestions}
        />
      )}

      {canBeEdited && (
        <CustomFieldsBox
          source={item}
          objectType={"QuoteItem"}
          actionButton={
            canEdit ? (
              <ModalOpenButton
                Modal={EditCustomFieldModal}
                modalProps={{
                  title: t("Edit quote item custom fields"),
                  objectId: item.id,
                  objectType: "QuoteItem",
                  customFields: item.customFields,
                  refetchQueries: onChangeCustomFieldsRefetchQueries,
                }}
              >
                <IconButton color="secondary" size="small">
                  <EditIcon />
                </IconButton>
              </ModalOpenButton>
            ) : undefined
          }
        />
      )}

      {hasEstimatedCalculationBox && canBeEdited && (
        <QuoteItemEstimatedCalculationBox
          key={`${item.product?.articleNumber}-${item.product?.supplierId}-${item.product?.title}`}
          projectId={project.id}
          docId={quote.id}
          item={item}
          isReadOnly={!canEdit}
          isManualSave={canManualSave && isManualSave}
          contractType={quote.contractType}
        />
      )}

      {hasSapS4HanaIntegration && (
        <S4HanaQuoteItemDataBox
          projectId={project.id}
          quoteId={quote.id}
          itemId={item.id}
          itemType={item.type}
          docViewerPermissions={quote.viewerPermissions}
          expandedItemIds={expandedItemIds}
          canEdit={canEdit}
        />
      )}

      {item.type === "decision" && canBeEdited && (
        <DecisionManagementBox
          item={item}
          doc={{
            id: quote.id,
            docType: "QUOTE",
            templateIsDraft: false,
            organisationId: quote.owningSystemOrganisationId,
          }}
          projectId={project.id}
          isEditable={canEdit}
          onItemDeleted={() => {
            navigate(
              { pathname: pathToDocPage, search: location.search },
              { replace: true }
            );
          }}
          refetchWizardQuestions={refetchWizardQuestions}
        />
      )}

      {canBeEdited && (
        <RestrictedByCapabilityWithDebug capability="executing">
          <RestrictedByDocumentPermissionWithDebug
            permission="quote_task_execute"
            document={quote}
          >
            <TaskManagementBox
              project={project}
              docId={quote.id}
              document={quote}
              item={item}
              canEdit={true}
            />
          </RestrictedByDocumentPermissionWithDebug>
        </RestrictedByCapabilityWithDebug>
      )}

      {canBeEdited && (
        <RestrictedByCapabilityWithDebug capability="executing">
          <RequiredOnCompletionBox
            project={project}
            docId={quote.id}
            document={quote}
            item={item}
            canEdit={true}
          />
        </RestrictedByCapabilityWithDebug>
      )}

      {canBeEdited && viewer.organisation.isCraftsmanOrganisation && (
        <RestrictedByCapabilityWithDebug capability="ordering">
          <RelatedOrderItemsBox
            orderItems={item.orderItems}
            project={project}
            document={quote}
          />
        </RestrictedByCapabilityWithDebug>
      )}

      {canBeEdited && (
        <ItemFilesBox
          projectId={project.id}
          docId={quote.id}
          docType="QUOTE"
          viewerPermissions={quote.viewerPermissions}
          item={item}
          onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
          attachmentFilter={
            // Do not show SAP attachments in normal attachment box
            attachment => attachment.group !== "sap-s4hana"
          }
        />
      )}

      {/* TODO: This attachment box should only be shown for specific Richter&Frenzel SAP Integration */}
      {canBeEdited && hasSapS4HanaIntegration && (
        <RfQuoteItemAttachmentsBox
          projectId={project.id}
          docId={quote.id}
          docType="QUOTE"
          viewerPermissions={quote.viewerPermissions}
          item={item}
          onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
        />
      )}

      {canBeEdited && (
        <SubelementsBox<QuoteItem_ChildItemFragment>
          projectId={project.id}
          doc={{ id: quote.id, docType: "QUOTE", templateIsDraft: false }}
          item={item}
          subitemComponent={ChildItemComponent}
          pathToDocPage={pathToDocPage}
          documentItemTypes={QUOTE_ITEM_TYPES}
          canChangeVisibility={true}
        />
      )}

      {canBeEdited && item.recommendations.length > 0 && (
        <RestrictedByCapabilityWithDebug capability="templating">
          <RestrictedByOrganisationPermissionWithDebug permission="template_usage_create">
            <RecommendationsBox
              projectId={project.id}
              item={item}
              documentItemTypes={QUOTE_ITEM_TYPES}
              expandedItemIds={expandedItemIds}
              onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
              refetchWizardQuestions={refetchWizardQuestions}
            />
          </RestrictedByOrganisationPermissionWithDebug>
        </RestrictedByCapabilityWithDebug>
      )}

      {canBeEdited && !(project.state === "opportunity") && item.placement && (
        <BuildingPlacementBox project={project} docId={quote.id} item={item} />
      )}

      {canBeEdited && item.contents.length > 0 && (
        <ContentBox
          itemId={item.id}
          content={item.contents}
          refetchQueries={onSetContentRefetchQueries}
          isEditable
        />
      )}

      {canBeEdited && (
        <RestrictedByCapabilityWithDebug capability="templating">
          <RestrictedByOrganisationPermissionWithDebug permission="template_usage_create">
            <RestrictedByCapabilityAndDocumentPermissionWithDebug
              capability="advancedTemplating"
              permission="quote_document_update_advancedTemplating"
              document={quote}
              otherwise={
                <TemplateTypePlaceholderBox
                  projectId={project.id}
                  doc={quote}
                  item={item}
                  isEditable={false}
                  onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
                  docType="QUOTE"
                  documentViewerPermissions={quote.viewerPermissions}
                  refetchWizardQuestions={refetchWizardQuestions}
                />
              }
            >
              <TemplateTypePlaceholderBox
                projectId={project.id}
                doc={quote}
                item={item}
                isEditable={canEdit}
                onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
                docType="QUOTE"
                documentViewerPermissions={quote.viewerPermissions}
                refetchWizardQuestions={refetchWizardQuestions}
              />
            </RestrictedByCapabilityAndDocumentPermissionWithDebug>
          </RestrictedByOrganisationPermissionWithDebug>
        </RestrictedByCapabilityWithDebug>
      )}
    </Stack>
  );
};

const createChildItemComponent =
  <ChildItem extends QuoteItem_ChildItemComponentFragment>(
    pathToDocPage: string,
    doc: {
      viewerPermissions: PermissionName[];
    },
    docAgreement: Agreement,
    getTo: (itemProps: SubitemComponentProps<ChildItem>) => To
  ) =>
  (itemProps: SubitemComponentProps<ChildItem>) => {
    const [Data123Left, Data123Right] = React.useMemo(
      () => getData123(itemProps.item, doc),
      [itemProps.item]
    );

    const RightIcons = React.useMemo(
      () => (
        <TreeRightIcons
          document={doc}
          item={itemProps.item}
          isVisibleToOtherSide={itemProps.item.isVisibleToOtherSide}
        />
      ),
      [itemProps.item]
    );

    return (
      <BareTreeItem
        docAgreement={docAgreement}
        Data123Left={Data123Left}
        Data123Right={Data123Right}
        RightIcons={RightIcons}
        showPath={!itemProps.item.isDecisionOption}
        item={itemProps.item}
        title={
          itemProps.item.pendingChangeAttributes?.title ?? itemProps.item.title
        }
        pathForPdf={
          itemProps.item.pendingChangeAttributes?.pathForPdf ??
          itemProps.item.pathForPdf
        }
        isRootItem={itemProps.isRootItem}
        depth={itemProps.depth}
        isGreyedOut={itemProps.isGreyedOut}
        isHidden={itemProps.isHidden}
        isPriceHidden={itemProps.isPriceHidden}
        subcontractPath={itemProps.subcontractPath}
        onClick={itemProps.onClick}
        to={itemProps.to ?? getTo(itemProps)}
      />
    );
  };

const QuoteItemEstimatedCalculationBox = ({
  projectId,
  docId,
  item,
  isReadOnly,
  isManualSave,
  ...props
}: Omit<
  React.ComponentProps<typeof EstimatedCalculationBox>,
  "projectId" | "manualSave" | "setIsEditing"
> & {
  projectId: string;
  isManualSave: boolean;
}) => {
  const { values, onValuesChanged, reset } =
    useProposedCalculationsWithOverride({
      projectId,
      docId,
      itemId: item.id,
    });

  const [isEditing, setIsEditing] = React.useState<boolean>(false);

  const manualSaveProps = React.useMemo(
    () =>
      isManualSave
        ? {
            manualSave: (
              <ManualSave
                onCancel={() => {
                  setIsEditing(false);
                  reset();
                }}
                onSubmitted={() => {
                  setIsEditing(false);
                  reset();
                }}
                onChange={(values: FormValues) => {
                  const override =
                    "materialBuyingPrice" in values
                      ? {
                          quantity: values.estimatedQuantity,
                          materialBuyingPrice: values.materialBuyingPrice,
                          materialMargin: values.materialMargin / 100,
                          time: values.estimatedTime,
                          workSellingRate: values.workSellingRate,
                          workBuyingRate: values.workBuyingRate,
                        }
                      : { quantity: values.estimatedQuantity };
                  onValuesChanged({ itemId: item.id, ...override });
                }}
              />
            ),
            setIsEditing,
          }
        : {},
    [isManualSave, item.id, onValuesChanged, reset]
  );

  return (
    <EstimatedCalculationBox
      projectId={projectId}
      docId={docId}
      item={
        isEditing
          ? merge({}, item, {
              proposedCalculation: values,
            })
          : item
      }
      isReadOnly={isReadOnly || (isManualSave && !isEditing)}
      {...manualSaveProps}
      {...props}
    />
  );
};
